import React, { useState, useContext, useEffect } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { Navigate, useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import { AuthContext } from "@healthdesk/shared-hooks";
import { useAuth, LocationContext } from "@healthdesk/shared-hooks";
import {
  capitalize,
  getTrialLabelForRole,
  isP2LikeRole,
  tokens,
} from "@healthdesk/shared-utils";
import {
  Alert,
  Box,
  Typography,
  Skeleton,
  Button,
  AlertTitle,
  Link,
  Paper,
} from "@mui/material";
import { LocalHospitalRounded, PersonRounded, MicRounded } from "@mui/icons-material";
import { InfoBox } from "../common";
// App-specific components - imported via webpack alias
import AddressSettings from "components/dashboard/AddressSettings";
import AccountSettings from "components/dashboard/AccountSettings";
import PhoneSettings from "components/dashboard/PhoneSettings";
import MarketingSettings from "components/dashboard/advertising/MarketingSettings";
import UsageDisplay from "components/dashboard/account/UsageDisplay";
import PlanManagement from "components/dashboard/account/PlanManagement";
import VoiceProviderToggle from "components/voice/VoiceProviderToggle";
import { formatDistanceToNow } from "date-fns";

const Settings = () => {
  const { user, userData, subscription } = useContext(AuthContext);
  const {
    hasActiveTrial,
    hasActiveDailyPass,
    hasValidSubscription,
    subscriptionTier,
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { location } = useContext(LocationContext);

  const [loading, setLoading] = useState();
  const [submitted, setSubmitted] = useState();

  const [addressOpen, setAddressOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [marketingOpen, setMarketingOpen] = useState(false);

  // Handle URL parameters for auto-opening dialogs
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "phone") {
      setPhoneOpen(true);
      // Clear the parameter to avoid reopening on refresh
      searchParams.delete("tab");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const sendToPortal = async (loadingKey = "portal") => {
    setLoading(loadingKey);

    const functionRef = firebase
      .app()
      .functions("us-central1")
      .httpsCallable("ext-firestore-stripe-payments-createPortalLink");
    const { data } = await functionRef({
      returnUrl: window.location.href,
    });
    window.location.assign(data.url);
  };

  const trialLabel = getTrialLabelForRole(userData?.role);
  const isFacility = userData?.role === "facility";

  return (
    <Box>
      <Typography
        variant="h4"
        component="h2"
        sx={{ mt: { xs: 1, sm: 5 }, mb: 2 }}
      >
        Settings
      </Typography>

      <Typography sx={{ mb: 4 }}>
        You're logged in as {user?.email}
        {userData?.role === "facility" && location?.title ? (
          <>
            {" "}
            using the <strong>{capitalize(location.title)}</strong> facility
            account.
          </>
        ) : (
          <>.</>
        )}
      </Typography>

      {submitted ? (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSubmitted(null)}
        >
          {submitted}
        </Alert>
      ) : null}

      {/* Daily Pass Status Display */}
      {hasActiveDailyPass && userData?.dailyPassExpiresAt && (
        <Alert severity="info" sx={{ pt: 1, mb: 3 }}>
          <Box>
            <AlertTitle>Daily Pass Active</AlertTitle>
            Your {userData?.subscriptionTier || "Medium"} Daily Pass expires{" "}
            {formatDistanceToNow(new Date(userData.dailyPassExpiresAt), {
              addSuffix: true,
            })}
            .
          </Box>
        </Alert>
      )}

      {/* Trial Status Display */}
      {hasActiveTrial && (
        <Alert severity="success" sx={{ pt: 1, mb: 3 }}>
          <Box>
            <AlertTitle>{trialLabel} Active.</AlertTitle>
            {isFacility
              ? "Enjoying CareMap Plus? Upgrade now to keep your waiting room operations connected after the trial."
              : "Enjoying Medical SuperIntelligence? Upgrade now to continue unlimited access after your trial ends."}
            <Button
              color="inherit"
              variant="outlined"
              onClick={() => navigate("/dashboard/upgrade")}
              sx={{ mt: 2, mb: 1, display: "block" }}
            >
              Upgrade
            </Button>
          </Box>
        </Alert>
      )}

      {/* Account Management section - for patient and other non-facility roles */}
      {userData?.role !== "facility" && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h3" sx={{ mb: 2 }}>
            Account Management
          </Typography>

          {/* Patient: link to subscription tiers & feature matrix (FEATURE_GATES–aligned explainer) */}
          {userData?.role === "patient" && (
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                p: 2,
                border: "1px solid rgba(27, 69, 132, 0.2)",
                borderLeft: `4px solid ${tokens.colors.primary.light}`,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                See what each plan includes and how usage limits work.
              </Typography>
              <Link
                component={RouterLink}
                to="/dashboard/plans-features"
                underline="hover"
                sx={{ color: "primary.main", fontWeight: 700 }}
              >
                Plans &amp; features (subscription levels)
              </Link>
            </Paper>
          )}

          {/* Usage Display - hidden for p2/p4 and advertiser, shown for patient and other non-facility roles */}
          {!isP2LikeRole(userData?.role) && userData?.role !== "advertiser" && (
            <UsageDisplay userData={userData} subscription={subscription} />
          )}

          {/* Plan Management - Only show for users with REAL subscription (not daily pass or trial) */}
          {hasValidSubscription &&
            !hasActiveTrial &&
            !hasActiveDailyPass && (
              <PlanManagement
                subscriptionTier={subscriptionTier}
                loading={loading}
                onPortal={sendToPortal}
                onChangePlan={() => navigate("/dashboard/upgrade")}
              />
            )}

          {/* Your Account - email and phone */}
          <InfoBox
            icon={<PersonRounded fontSize="large" color="primary" />}
            title="Your Account"
            description="Manage your account settings and contact information."
            links={[
              {
                title: "Update Email",
                onClick: () => setAccountOpen(true),
              },
              {
                title: userData?.phoneVerified
                  ? "Update Phone Number"
                  : "Verify Phone Number",
                onClick: () => setPhoneOpen(true),
              },
            ]}
          />
        </Box>
      )}

      {/* Facility Info */}
      {userData?.role === "facility" && (
        <>
          <InfoBox
            icon={
              <LocalHospitalRounded
                fontSize="large"
                sx={{ color: "secondary.main" }}
              />
            }
            title="Your Facility"
            description="Manage your facility location, group settings for multi-location maps, and advertising profile."
            links={[
              {
                title: "Manage Facility & Location",
                onClick: () => setAddressOpen(true),
              },
              {
                title: "Update Your Advertising Profile",
                onClick: () => setMarketingOpen(true),
              },
            ]}
          />

          {/* Multi-Location Map URL Alert - Only show if group name is set */}
          {location?.group && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Patients can view only your {location.group} facilities at{" "}
              <Link
                href={`${window.location.origin}/map/${location.group
                  .toLowerCase()
                  .replace(/[^a-z0-9\s\-'&]/g, "")
                  .replace(/[\s'&]+/g, "-")
                  .replace(/-+/g, "-")
                  .replace(/^-|-$/g, "")}`}
                target="_blank"
                variant="body2"
              >
                {window.location.origin}/map/
                {location.group
                  .toLowerCase()
                  .replace(/[^a-z0-9\s\-'&]/g, "")
                  .replace(/[\s'&]+/g, "-")
                  .replace(/-+/g, "-")
                  .replace(/^-|-$/g, "")}
              </Link>
              .
            </Alert>
          )}

          {/* Plan Management for facilities - Only show for users with REAL subscription (not daily pass or trial) */}
          {hasValidSubscription && !hasActiveTrial && !hasActiveDailyPass && (
            <PlanManagement
              subscriptionTier={subscriptionTier}
              loading={loading}
              onPortal={sendToPortal}
              onChangePlan={() => navigate("/dashboard/upgrade")}
            />
          )}
        </>
      )}

      {/* Your Account - for facility only (non-facility see it under Account Management above) */}
      {userData?.role === "facility" && (
        <InfoBox
          icon={<PersonRounded fontSize="large" color="primary" />}
          title="Your Account"
          description="Manage your account settings and contact information."
          links={[
            {
              title: "Update Email",
              onClick: () => setAccountOpen(true),
            },
            {
              title: userData?.phoneVerified
                ? "Update Phone Number"
                : "Verify Phone Number",
              onClick: () => setPhoneOpen(true),
            },
          ]}
        />
      )}

      {/* Voice Provider Settings - ChartMind STT/TTS; hidden for p2/p4, advertiser, and lotsog (LOTSOG briefing voice lives in LOTSOG briefing card above). */}
      {!isP2LikeRole(userData?.role) && userData?.role !== "advertiser" && userData?.role !== "lotsog" && (
        <InfoBox
          icon={<MicRounded fontSize="large" sx={{ color: tokens.colors.uiPurple.light }} />}
          title="Voice Settings"
          description="Configure speech recognition (STT) and text-to-speech (TTS) providers for ChartMind and other voice features."
        >
          <Box sx={{ mt: 2 }}>
            <VoiceProviderToggle />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Deepgram offers lower latency, better medical terminology recognition, and code-switching support for multilingual encounters.
            </Typography>
          </Box>
        </InfoBox>
      )}

      {/* Settings Dialogs */}
      <AddressSettings
        user={user}
        data={userData}
        location={location}
        visible={addressOpen}
        close={() => setAddressOpen(false)}
        setSubmitted={setSubmitted}
      />

      <MarketingSettings
        user={user}
        data={userData}
        location={location}
        visible={marketingOpen}
        close={() => setMarketingOpen(false)}
        setSubmitted={setSubmitted}
      />

      <AccountSettings
        user={user}
        userData={userData}
        visible={accountOpen}
        close={() => setAccountOpen(false)}
        setSubmitted={setSubmitted}
      />

      <PhoneSettings
        user={user}
        userData={userData}
        visible={phoneOpen}
        close={() => setPhoneOpen(false)}
        setSubmitted={setSubmitted}
      />
    </Box>
  );
};

export default Settings;
